import {
  AnalyticsComponent,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import {
  ExploreAnalyticsEvent,
  ExploreAnalyticsSources,
} from "@/constants/analytics/Explore";
import { ExploreTabSubDeepLinks } from "@/constants/Explore";
import { ModalPath } from "@/constants/Navigation";
import { DeepLinkParams } from "@/constants/Params";
import { router } from "@/crossPlatform/expo-router";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import usePatientStatus from "@/hooks/usePatientStatus";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import { useToast } from "@/hooks/useToast";
import { captureException } from "@/services/AppMonitor";
import { useGetGroupClassesQuery } from "@/store/api/schedule/groupClasses";
import { useGetFullProfileQuery } from "@/store/api/user";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { useGroupClassDetailsModalDispatch } from "@/store/reducers/modals/groupClassDetailsModal";

const useSearchGroupsSubDeepLink = ({ onOpen }: { onOpen: () => void }) => {
  const { showToast } = useToast();
  const { data: fullProfileData } = useGetFullProfileQuery();
  const { isCurrentOrSupportLinkedUserInTreatment } =
    usePatientStatus(fullProfileData);
  const { [DeepLinkParams.GroupId]: groupId } = useEncodedSearchParams<{
    [DeepLinkParams.GroupId]?: string;
  }>();

  const { setGroupClassDetailsData } = useGroupClassDetailsModalDispatch();
  const { externalId: appUserExternalId, isSupport: isAppUserSupport } =
    useAppUserSelector();
  const patientIdForQuery = useScheduleQueryPatientId(appUserExternalId);
  const { data, isLoading } = useGetGroupClassesQuery(
    {
      patientId: patientIdForQuery,
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    { skip: !groupId },
  );

  const onOpenSubDeepLink = () => {
    if (!groupId) {
      onOpen();
      return;
    }

    const groupClass = data?.find(
      (gc: GroupClassSeries) => gc.groupClassExternalId === groupId,
    );
    if (!groupClass) {
      captureException(
        new Error(
          `Group class details deep link with groupId ${groupId} failed to load`,
        ),
      );
      showToast({
        text: "Couldn’t load this group class",
        variant: "error",
      });
      return;
    }
    setGroupClassDetailsData({
      groupClassExternalId: groupId,
      isAppUserSupport,
      patientId: appUserExternalId,
      source: GroupClassSeriesAnalyticsSources.DeepLink,
    });
    router.navigate(ModalPath.GroupClassDetails);
  };

  useSubDeepLink({
    analyticsEvent: groupId
      ? ExploreAnalyticsEvent.GroupClassDetailsOpenedFromDeepLink
      : ExploreAnalyticsEvent.SearchGroupsOpenedFromDeepLink,
    analyticsProps: {
      component: AnalyticsComponent.Tab,
      source: ExploreAnalyticsSources.ExploreScreen,
    },
    onOpen: onOpenSubDeepLink,
    shouldHandle: isCurrentOrSupportLinkedUserInTreatment && !isLoading,
    targetValue: ExploreTabSubDeepLinks.SearchGroups,
  });
};

export default useSearchGroupsSubDeepLink;
